/**
 * Alerts API Service
 *
 * Handles all alert-related API operations.
 * Services are stateless and return promises - UI concerns belong in hooks.
 *
 * @see ../ARCHITECTURE.md for design principles
 */

import { authenticatedFetch } from '../../utils/authenticated-fetch';
import { AlertApiError, type NotifyAlertParams, type NotifyAlertResult } from './types';

/**
 * Dismiss an alert (mark as dismissed without resolving)
 *
 * @param alertId - The ID of the alert to dismiss
 * @throws AlertApiError if the operation fails
 */
export async function dismissAlert(alertId: number): Promise<void> {
  const response = await authenticatedFetch(`/api/alerts/${alertId}/dismiss`, {
    method: 'POST',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({})) as { error?: string; message?: string };
    throw new AlertApiError(
      errorData.error || errorData.message || 'Failed to dismiss alert',
      response.status,
      alertId
    );
  }
}

/**
 * Resolve an alert (mark as resolved - issue addressed)
 *
 * @param alertId - The ID of the alert to resolve
 * @throws AlertApiError if the operation fails
 */
export async function resolveAlert(alertId: number): Promise<void> {
  const response = await authenticatedFetch(`/api/alerts/${alertId}/resolve`, {
    method: 'POST',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({})) as { error?: string; message?: string };
    throw new AlertApiError(
      errorData.error || errorData.message || 'Failed to resolve alert',
      response.status,
      alertId
    );
  }
}

/**
 * Send notification for an alert to specified channels
 *
 * @param params - Alert ID and channel IDs to notify
 * @returns Result with sent and skipped channel details
 * @throws AlertApiError if the operation fails
 */
export async function notifyAlert(params: NotifyAlertParams): Promise<NotifyAlertResult> {
  const { alertId, channelIds } = params;

  const response = await authenticatedFetch(`/api/alerts/${alertId}/notify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ channel_ids: channelIds }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({})) as { error?: string; message?: string };
    throw new AlertApiError(
      errorData.error || errorData.message || 'Failed to send notification',
      response.status,
      alertId
    );
  }

  return response.json() as Promise<NotifyAlertResult>;
}
